import React, { lazy, ReactElement } from 'react'
import { Route, Router, Switch } from 'react-router-dom'
import PublicEnrollment from 'public-pages/enrollment/public-enrollment/PublicEnrollment'
import Login from './public-pages/login/Login'
import SecureLayout from 'secure-layout'
import Access from 'secure-pages/access'
import { RouterConfig } from 'core/utils/constants'
import { BasePathConst } from 'configs/Path.configs'
import History from 'core/history/history'
import Redirecting from 'secure-pages/redirecting'
import SetupLicense from 'public-pages/license/setup-license'
import SetupUser from 'public-pages/setup-user'
import Agreement from './secure-pages/agreement'

// tmp do not lazy load the following main pages
import Users from './secure-pages/users'
import AuditLogs from './secure-pages/reporting/audit-logs/AuditLogs'
import Settings from './secure-pages/settings/Settings'
import MyServices from './secure-pages/my-services/MyServices'
import CreateUserWithRole from './secure-pages/users/create-user-with-role/CreateUserWithRole'
import UpdateLicense from './public-pages/license/update-license'
import EditProfile from './secure-pages/profile/edit-profile/EditProfile'
import EditUserWithRole from './secure-pages/users/edit-user-with-role/EditUserWithRole'
import OauthAdd from './secure-pages/settings/oauth-add/OauthAdd'
import AuditEventsDetail from './secure-pages/reporting/audit-logs-detail/AuditEventsDetail'
import Tenants from './secure-pages/tenants/Tenants.page';
import UserDetails from './secure-pages/users/user-details/UserDetails'
import Tenant from './secure-pages/tenants/tenant/Tenant';
import PageNotFound from './secure-pages/page-not-found/PageNotFound'
import UserAccessLayout from 'secure-pages/users/UserAccessLayout'
import GroupDetails from './secure-pages/groups/group-details/GroupDetails'
import  { BillingAndSubscription, Cart } from './secure-pages/ecommerce/EcommerceRouteComponents';
import UnifiedUserManagement from './secure-pages/unified-user-management/UnifiedUserManagement';
import Enviroments from './secure-pages/enviroments/Environments';



const AppRoutes = (): ReactElement => {
  const SelfServiceUser = lazy(() => import('./public-pages/self-service-user/SelfServiceUser'))
  const GenerateClientAuth = lazy(() => import('./secure-pages/client-auth'));
  const Verification = lazy(() => import('./public-pages/verification/Verification'));
  const AdminProfile = lazy(() => import('./secure-pages/profile'));
  const EditUser = lazy(() => import('./secure-pages/users/edit-user/EditUser'));
  const ClientAuthLoginVerificationError = lazy(() => import('./public-pages/errors/ClientAuthLoginVerificationError'));
  const SamlVerificationError = lazy(() => import('./public-pages/errors/SamlVerificationError'));
  const UpdateLicenseError = lazy(() => import('./public-pages/errors/UpdateLicenseError'));
  const AccountExpiredError = lazy(() => import('./public-pages/errors/AccountExpiredError'));
  const CertificateObtaining = lazy(() => import('./public-pages/certificate/CertificateObtaining'));
  const EditTenant = lazy(() => import('./secure-pages/tenants/edit/EditTenant'));
  const AccountSigningClientCreate = lazy(() => import('./secure-pages/tenants/create-account-signing-client/AccountSigningClient'));
  const AccountSigningClientEdit = lazy(() => import('./secure-pages/tenants/create-account-signing-client/edit/AccountSigningClientEdit'));


  // todo delete after refactor: settings page is still dependent on old 2FA, CreateAccountSignIn, editStandardSignIn
  const CreateAccountSignIn = lazy(() => import('./secure-pages/tenants/create-account-sign-in'));
  const EditStandardSignIn = lazy(() => import('./secure-pages/tenants/create-account-sign-in/edit/EditStandardSignIn'));
  // end todo delete after refactor

  const AccountOauthAdd = lazy(() => import('./secure-pages/settings/oauth-add/OauthAdd'));

  return (
    <Router history={History}>
      <Switch>
        {/* Public routes */}
        <Route exact path={RouterConfig.urlMap.setupLicense} component={SetupLicense} />
        <Route exact path={`${BasePathConst}/enrollment/:enrollmentId/:enrollmentCode`} component={PublicEnrollment} />
        <Route exact path={`${BasePathConst}/create-user-account`} component={SelfServiceUser} />
        <Route exact path={RouterConfig.urlMap.setupUser} component={SetupUser} />
        <Route exact path={RouterConfig.urlMap.login} component={Redirecting} />
        <Route exact path={`${BasePathConst}/verify/:userId/:verificationCode`} component={Verification} />
        <Route exact path={`${BasePathConst}/login`} component={Login} />
        <Route exact path={`${BasePathConst}`} component={() => {
          window.location.href = `${BasePathConst}/my-services`
          return null
        }} />

        <Route exact path={`/`} component={() => {
          window.location.href = `${BasePathConst}/my-services`
          return null
        }} />

        <Route
          exact
          path={`${BasePathConst}/certificate-pickup`}
          component={CertificateObtaining}
        />

        <Route path={RouterConfig.urlMap.samlVerificationError} component={SamlVerificationError} />
        <Route path={RouterConfig.urlMap.clientAuthLoginVerificationError} component={ClientAuthLoginVerificationError} />
        <SecureLayout exact path={RouterConfig.urlMap.updateLicenseError} component={UpdateLicenseError} />
        <Route exact path={RouterConfig.urlMap.accountExpiredError} component={AccountExpiredError} />

        <Route path='/app/imauth/api/v1/logout' component={Redirecting} />
        <Route path='/app/imauth/api/v1/login' component={Redirecting} />
        <Route path='/certificate-authority' component={Redirecting} />
        <Route path='/mpki/dashboard' component={Redirecting} />
        <Route path='/iot/dashboard' component={Redirecting} />
        <Route path='/signingmanager' component={Redirecting} />

        {/* Agreement */}
        <Route exact path={`${BasePathConst}/agreement/:userId`} component={Agreement} />

        {/* Admin profile */}
        <SecureLayout exact path={RouterConfig.urlMap.adminProfile} component={AdminProfile} />
        <SecureLayout exact path={RouterConfig.urlMap.editAdminProfile} component={EditProfile} />

        {/* Access */}
        <SecureLayout exact path={RouterConfig.urlMap.access} component={Access} />

        {/* Administrators */}
        <SecureLayout exact path={RouterConfig.administrators.table} component={Users} />
        <SecureLayout exact path={RouterConfig.administrators.rolesEdit()} component={EditUserWithRole} />
        <SecureLayout exact path={RouterConfig.administrators.createUserWithRole} component={CreateUserWithRole} />
        <SecureLayout exact path={RouterConfig.administrators.details()} component={UserDetails} />
        <SecureLayout exact path={RouterConfig.administrators.services()} component={UserDetails} />
        <SecureLayout exact path={RouterConfig.administrators.credentials()} component={UserDetails} />
        <SecureLayout exact path={RouterConfig.administrators.edit()} component={EditUser} />
        {/* unified microfrontend - feature flag gating handled inside the component */}
        <SecureLayout
          path={RouterConfig.urlMap.unifiedUserManagement}
          component={UnifiedUserManagement}
        />
        {/* billing and subscription microfrontend - only for eCommerce customers */}
        <SecureLayout
          path={RouterConfig.urlMap.billingAndSubscription}
          component={BillingAndSubscription}
        />

        {/* Cart */}
        <SecureLayout
          path={RouterConfig.urlMap.cart}
          component={Cart}
        />

        {/* Groups */}
        <SecureLayout exact path={RouterConfig.groups.table} component={UserAccessLayout} />
        <SecureLayout exact path={RouterConfig.groups.details()} component={GroupDetails} />
        <SecureLayout exact path={RouterConfig.groups.groupAccess()} component={GroupDetails} />

        {/* Client Auth */}
        <SecureLayout exact path={RouterConfig.administrators.clientAuth()} component={GenerateClientAuth} />
        <SecureLayout exact path={RouterConfig.serviceUser.clientAuth()} component={GenerateClientAuth} />

        {/* Accounts */}
        <SecureLayout exact path={RouterConfig.accounts.table} component={Tenants} />
        <SecureLayout exact path={RouterConfig.accounts.details()} component={Tenant} />
        <SecureLayout exact path={RouterConfig.accounts.services()} component={Tenant} />
        <SecureLayout exact path={RouterConfig.accounts.identityAndAccess()} component={Tenant} />
        <SecureLayout exact path={RouterConfig.accounts.edit()} component={EditTenant} />

        {/* DELETE THIS FILE AFTER REFACTOR */}
        <SecureLayout exact path={RouterConfig.accounts.editStandardSignIn()} component={EditStandardSignIn} />
        {/* END DELETE THIS FILE AFTER REFACTOR */}

        <SecureLayout exact path={RouterConfig.accounts.createAccountSigningClient()} component={AccountSigningClientCreate} />
        <SecureLayout exact path={RouterConfig.accounts.editAccountSigningClient()} component={AccountSigningClientEdit} />
        <SecureLayout exact path={RouterConfig.accounts.addOauth()} component={AccountOauthAdd} />


        {/* Settings */}
        <SecureLayout exact path={RouterConfig.platformSettings.settings} component={Settings} />
        <SecureLayout exact path={RouterConfig.platformSettings.addOauth()} component={OauthAdd} />
        <SecureLayout exact path={RouterConfig.platformSettings.updateLicense} component={UpdateLicense} />
        <SecureLayout exact path={RouterConfig.platformSettings.updateStandardSignIn()} component={EditStandardSignIn} /> {/* DELETE THIS FILE AFTER REFACTOR */}
        <SecureLayout exact path={RouterConfig.platformSettings.addAccountSignIn()} component={CreateAccountSignIn} />

        {/* Reporting */}
        <SecureLayout exact path={RouterConfig.logging.table} component={AuditLogs} />
        <SecureLayout exact path={RouterConfig.logging.details()} component={AuditEventsDetail} />


        {/* Enviroment */}
        <SecureLayout exact path={RouterConfig.urlMap.environments} component={Enviroments} />


        {/* My services */}
        <SecureLayout exact path={RouterConfig.urlMap.myServices} component={MyServices} />

        {/* Page not found all other routes should be placed above*/}
        <SecureLayout path='' component={PageNotFound} />
      </Switch>
    </Router>
  )
}

export default AppRoutes
